import { useEffect, useState, createContext, useContext, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";

// Pages
import HomePage from "./pages/HomePage";
import VenueListPage from "./pages/VenueListPage";
import VenueDetailPage from "./pages/VenueDetailPage";
import EventListPage from "./pages/EventListPage";
import EventDetailPage from "./pages/EventDetailPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import CustomerDashboard from "./pages/customer/CustomerDashboard";
import OwnerDashboard from "./pages/owner/OwnerDashboard";
import AdminDashboard from "./pages/admin/AdminDashboard";
import MaintenanceGate from "./components/MaintenanceGate";
import { BrandingProvider } from "./contexts/BrandingContext";
import DynamicFavicon from "./components/DynamicFavicon";
import CmsPage from "./pages/CmsPage";
import VenueMapPage from "./pages/VenueMapPage";
import AcceptInvitationPage from "./pages/AcceptInvitationPage";
import PricingPage from "./pages/PricingPage";
import ContactPage from "./pages/ContactPage";
import { CollectionsListPage, CollectionDetailPage } from "./pages/CollectionsPage";
import i18n from "./i18n";

// API base resolution:
// In production we deploy the frontend behind multiple hosts (preview URL,
// emergent.host, and the customer's custom domain like tablecy.com). Every host
// proxies /api/* to the same backend. If we hard-code REACT_APP_BACKEND_URL,
// auth cookies get set on whichever host the OAuth callback lands on but won't
// be sent back on cross-origin XHRs from a different host (e.g. cookies set on
// emergent.host aren't sent when the user is browsing tablecy.com). Using the
// current window origin keeps every API request first-party to the host the
// user is actually on, which eliminates the cookie / CORS mismatch.
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = (typeof window !== "undefined" && window.location?.origin)
  ? window.location.origin
  : ENV_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Auto-attach the active venue_id to all /api/owner/* requests so multi-venue
// owners can switch context without every page knowing about it.
axios.interceptors.request.use((config) => {
  try {
    const url = config.url || "";
    if (url.includes("/api/owner/")) {
      const activeVenue = localStorage.getItem("active_venue_id");
      if (activeVenue) {
        config.params = { ...(config.params || {}), venue_id: activeVenue };
      }
    }
  } catch (e) {
    // localStorage unavailable — ignore
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Format API error detail helper
export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the session check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      // /auth/session returns 200 with {authenticated, user} — no 401 console noise for anonymous visitors
      const { data } = await axios.get(`${API}/auth/session`);
      setUser(data?.authenticated ? data.user : null);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Sync active language with user's stored preferred_language on login/load
  useEffect(() => {
    const pref = user?.preferred_language;
    if (pref && (pref === "en" || pref === "el") && pref !== i18n.language) {
      i18n.changeLanguage(pref);
    }
  }, [user?.preferred_language]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    return data;
  };

  const register = async (email, password, name, phone) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name, phone });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Ignore logout errors
    }
    setUser(null);
  };

  const value = {
    user,
    setUser,
    loading,
    login,
    register,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin" || user?.role === "super_admin",
    isOwner: user?.role === "venue_owner",
    isCustomer: user?.role === "customer",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// OAuth Callback Handler
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processOAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/login", { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];
      
      try {
        const { data } = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        setUser(data);
        
        // Clear hash and redirect to dashboard
        window.history.replaceState(null, "", window.location.pathname);
        
        if (data.role === "admin" || data.role === "super_admin") {
          navigate("/admin", { replace: true, state: { user: data } });
        } else if (data.role === "venue_owner") {
          navigate("/owner", { replace: true, state: { user: data } });
        } else {
          navigate("/account", { replace: true, state: { user: data } });
        }
      } catch (error) {
        console.error("OAuth error:", error);
        navigate("/login", { replace: true });
      }
    };

    processOAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <MaintenanceGate>
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/venues" element={<VenueListPage />} />
      <Route path="/venues/city/:citySlug" element={<VenueListPage />} />
      <Route path="/venues/type/:typeSlug" element={<VenueListPage />} />
      <Route path="/venues/cuisine/:cuisineSlug" element={<VenueListPage />} />
      {/* Canonical venue URL: /venue/{district_slug}/{area_slug}/{venue_slug}.
          Old /venue/:slug routes hard-404 per migration plan. */}
      <Route path="/venue/:districtSlug/:areaSlug/:venueSlug" element={<VenueDetailPage />} />
      <Route path="/events" element={<EventListPage />} />
      <Route path="/events/today" element={<EventListPage filter="today" />} />
      <Route path="/event/:eventSlug" element={<EventDetailPage />} />
      <Route path="/page/:slug" element={<CmsPage />} />
      <Route path="/map" element={<VenueMapPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/collections" element={<CollectionsListPage />} />
      <Route path="/collections/:slug" element={<CollectionDetailPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Customer Dashboard */}
      <Route
        path="/account/*"
        element={
          <ProtectedRoute allowedRoles={["customer", "venue_owner", "admin", "super_admin"]}>
            <CustomerDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Owner Dashboard */}
      <Route
        path="/owner/*"
        element={
          <ProtectedRoute allowedRoles={["venue_owner", "admin", "super_admin"]}>
            <OwnerDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Admin Dashboard */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </MaintenanceGate>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingProvider>
          <DynamicFavicon />
          <AppRouter />
          <Toaster position="top-right" richColors closeButton />
        </BrandingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
